import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import Icon from '../../../Icon';
import MapIcon from '../../../../public/static/images/new_search/icons/map-icon.svg';
import ListIcon from '../../../../public/static/images/new_search/icons/show-as-list.svg';
import useScreenSize from '../../../../hooks/useScreenSize';
import { MapViewMode, MapViewQueryParam } from '../../../ItemsMap/types';
import { MapButton, MapLinkText } from './styled';

interface ItemsViewToggleLinkProps {
  isMapView: boolean;
  query: MapViewQueryParam;
  cypress: string;
  stat: string;
}

export const ItemsViewToggleLink: React.FC<ItemsViewToggleLinkProps> = ({
  isMapView,
  query,
  cypress,
  stat
}) => {
  const { t } = useTranslation();
  const { isSmall } = useScreenSize();
  const router = useRouter();
  const shouldScrollRef = useRef(false);

  const nextUrl = useMemo(() => {
    const [path, queryString = ''] = router.asPath.split('?');
    const params = new URLSearchParams(queryString);

    params.set(query, isMapView ? MapViewMode.list : MapViewMode.map);

    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  }, [router.asPath, isMapView, query]);

  const handleClick = (e: MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();

    shouldScrollRef.current = true;

    router.push(nextUrl, undefined, {
      shallow: true,
      scroll: false
    });
  };

  useEffect(() => {
    if (isMapView && shouldScrollRef.current) {
      const mapElement = document.getElementById('items-map');
      const bottomOffset = 40;

      const mapHeight = isSmall ? 470 : 660;

      if (mapElement) {
        const elementTop =
          mapElement.getBoundingClientRect().top + window.pageYOffset;

        const elementBottom = elementTop + mapHeight;

        const scrollPosition =
          elementBottom - window.innerHeight + bottomOffset;

        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }

      shouldScrollRef.current = false;
    }
  }, [isMapView, isSmall]);

  return (
    <MapButton
      type="button"
      onClick={handleClick}
      rel="noopener noreferrer"
      data-cy={cypress}
      data-stat={stat}
      $isMapView={isMapView}
    >
      <Icon
        IconComponent={isMapView ? ListIcon : MapIcon}
        width={20}
        height={20}
        indent={false}
        color="white"
      />
      <MapLinkText tag="body_1">
        {t(isMapView ? 'new_search.list' : 'new_search.map')}
      </MapLinkText>
    </MapButton>
  );
};
